import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ArrowDown,
  CheckCircle2,
  Eye,
  EyeOff,
  Fingerprint,
  Hand,
  Hourglass,
  Keyboard,
  MousePointer2,
  Send,
  Smartphone,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

interface EventRow {
  id: string;
  auditId: string;
  tsOffsetMs: number;
  createdAt: number;
  kind: string;
  data: string;
}

const KIND_INFO: Record<
  string,
  { label: string; icon: typeof Activity; tone: 'good' | 'bad' | 'neutral' | 'warn' }
> = {
  pageView: { label: '页面加载', icon: Eye, tone: 'neutral' },
  fingerprintStart: { label: '开始采集指纹', icon: Fingerprint, tone: 'neutral' },
  fingerprintDone: { label: '指纹采集完成', icon: Fingerprint, tone: 'good' },
  challengeShown: { label: '显示 Continue 按钮', icon: Sparkles, tone: 'neutral' },
  challengeClicked: { label: '✋ 点击 Continue (鼠标)', icon: MousePointer2, tone: 'good' },
  challengeTouched: { label: '👆 点击 Continue (触摸)', icon: Hand, tone: 'good' },
  challengeTimeout: { label: '⏰ 挑战超时未点击', icon: Hourglass, tone: 'bad' },
  firstMouseMove: { label: '首次鼠标移动', icon: MousePointer2, tone: 'good' },
  firstTouch: { label: '首次触摸', icon: Hand, tone: 'good' },
  firstScroll: { label: '首次滚动', icon: ArrowDown, tone: 'good' },
  firstKey: { label: '首次按键', icon: Keyboard, tone: 'neutral' },
  visibilityChange: { label: '页面可见性切换', icon: EyeOff, tone: 'warn' },
  motionFirstSample: { label: '首次加速度计采样', icon: Smartphone, tone: 'good' },
  orientationFirstSample: { label: '首次陀螺仪采样', icon: Smartphone, tone: 'good' },
  verifyRequested: { label: '发起 verify 请求', icon: Send, tone: 'neutral' },
  redirectStarted: { label: '🚀 开始跳转', icon: Send, tone: 'neutral' },
  redirectAttempt: { label: '尝试唤起系统浏览器', icon: Send, tone: 'neutral' },
  redirectAppSwitched: { label: '✅ 已唤起系统浏览器', icon: CheckCircle2, tone: 'good' },
  redirectFallback: { label: '⚠️ 唤起未确认,尝试备用方式', icon: Hourglass, tone: 'warn' },
  redirectLateSwitch: { label: '✅ 唤起成功(晚切,已校准)', icon: CheckCircle2, tone: 'good' },
  redirectGiveUp: { label: '❌ 唤起放弃,留在 webview', icon: XCircle, tone: 'bad' },
  redirectInline: { label: '↪️ 在当前 webview 跳转', icon: Send, tone: 'neutral' },
};

const TONE_CLASSES: Record<'good' | 'bad' | 'neutral' | 'warn', string> = {
  good: 'border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  bad: 'border-red-500/40 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  warn: 'border-amber-500/40 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  neutral:
    'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200',
};

export function EventTimeline({ auditId }: { auditId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['audit-events', auditId],
    queryFn: () => api<{ rows: EventRow[] }>(`/api/audits/${auditId}/events`),
  });

  if (isLoading) return <div className="text-sm text-zinc-500">加载中…</div>;
  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-zinc-300 p-4 text-sm text-zinc-500 dark:border-zinc-700">
        无埋点交互记录(本次访问发生在埋点功能上线之前,或访客无任何交互)
      </div>
    );
  }

  // Check if user clicked the continue button
  const clicked = rows.some((r) => r.kind === 'challengeClicked' || r.kind === 'challengeTouched');
  const timedOut = rows.some((r) => r.kind === 'challengeTimeout');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-zinc-500">用户是否点击 Continue:</span>
        {clicked ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> 是
          </span>
        ) : timedOut ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/60 dark:text-red-300">
            <XCircle className="h-3 w-3" /> 否,挑战超时
          </span>
        ) : (
          <span className="text-xs text-zinc-500">挑战未开启</span>
        )}
      </div>

      <ol className="relative space-y-2 border-l border-zinc-200 pl-6 dark:border-zinc-700">
        {rows.map((r) => {
          const info = KIND_INFO[r.kind] ?? {
            label: r.kind,
            icon: Activity,
            tone: 'neutral' as const,
          };
          const Icon = info.icon;
          let payload = '';
          try {
            const d = JSON.parse(r.data) as Record<string, unknown>;
            if (Object.keys(d).length) {
              payload = Object.entries(d)
                .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
                .join(' ');
            }
          } catch {
            // ignore
          }
          return (
            <li key={r.id} className="relative max-w-full">
              <span className="absolute -left-[34px] flex h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                <Icon className="h-3 w-3 text-zinc-500" />
              </span>
              <div
                className={cn(
                  'flex max-w-full items-baseline gap-2 rounded-md border px-2.5 py-1 text-sm',
                  TONE_CLASSES[info.tone],
                )}
              >
                <span className="shrink-0 whitespace-nowrap font-mono text-xs opacity-70">
                  +{(r.tsOffsetMs / 1000).toFixed(2)}s
                </span>
                <span className="shrink-0 whitespace-nowrap">{info.label}</span>
                {payload && (
                  <span
                    className="min-w-0 truncate whitespace-nowrap font-mono text-xs opacity-60"
                    title={payload}
                  >
                    {payload}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
